import { Tab } from "@headlessui/react";
import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import { fetchPrice } from "~/lib/astrolescent";
import { setDbFromContext } from "~/lib/db";
import { getWeeklyTippers } from "~/lib/models/tipWeekly";
import { getRichlist } from "~/lib/richlist";
import { fetchTopPlayers } from "~/lib/claims/hug-monsters";
import {
  classNames,
  HUG_RESOURCE_ADDRESS,
  prettyValue,
  shortenAddress,
} from "~/lib/util";

export async function loader({ context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  const { sortedTodayList, sortedSundayList, sortedAlltimeList } =
    await fetchTopPlayers();

  return json({ sortedTodayList, sortedSundayList, sortedAlltimeList });
}

export const meta: MetaFunction = () => {
  return [{ title: "Play Hug Monsters", description: "How high can you fly?" }];
};

export default function LeaderboardPage() {
  const { sortedTodayList, sortedSundayList, sortedAlltimeList } =
    useLoaderData<typeof loader>();

  return (
    <div className="w-4/5 lg:w-full mx-auto max-w-[984px]">
      <div className="space-y-4 relative pb-[530px]">
        <h1 className="text-[60px] font-bold text-center bangers p-8 pb-0 text-white">
          Hug Monsters
        </h1>
        <p className="text-center mt-6 yaelah text-white text-[18px]">
          Huggy's in a pickle; he's being swarmed by monsters. Help him survive
          and reach the top!
        </p>
        <p className="text-center mt-6 yaelah text-white text-[18px]">
          Play the game, earn HUGs. What's not to love?!
        </p>

        {/* Add extra space and wider wrapper for the play image */}
        <div
          className="relative flex justify-center mt-64"
          style={{ marginTop: "4rem" }}
        >
          <div className="max-w-[1100px] w-full -mx-4">
            <a
              href="https://t.me/Moxstd_bot/hugmonsters"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src="/images/play-hug-monsters.png"
                className="mx-auto cursor-pointer w-full"
                alt="Play Hug Monsters"
              />
            </a>
          </div>
        </div>

        <h1 className="text-[60px] font-bold text-center bangers p-8 pb-0 text-white">
          Leaderboard
        </h1>

        <Tab.Group defaultIndex={1}>
          <div className="p-1.5 bg-white/80 rounded-xl">
            <Tab.List className="flex space-x-0 rounded-xl p-1">
              <Tab
                key="topScorersDaily"
                className={({ selected }) =>
                  classNames(
                    "w-full rounded-l-lg py-3 text-sm font-medium leading-5",
                    "ring-white/60 text-[#747474] text-[16px] poppins-medium focus:outline-none",
                    selected
                      ? "bg-white shadow"
                      : "bg-black/[0.1] hover:text-white"
                  )
                }
              >
                Daily
              </Tab>
              <Tab
                key="topScorersWeekly"
                className={({ selected }) =>
                  classNames(
                    "w-full py-3 text-sm font-medium leading-5",
                    "ring-white/60 text-[#747474]  text-[16px] poppins-medium focus:outline-none",
                    selected
                      ? "bg-white shadow"
                      : "bg-black/[0.1] hover:text-white"
                  )
                }
              >
                Weekly
              </Tab>
              <Tab
                key="topScorersAlltime"
                className={({ selected }) =>
                  classNames(
                    "w-full rounded-r-lg py-3 text-sm font-medium leading-5",
                    "ring-white/60 text-[#747474] text-[16px] poppins-medium focus:outline-none",
                    selected
                      ? "bg-white shadow"
                      : "bg-black/[0.1] hover:text-white"
                  )
                }
              >
                All-time
              </Tab>
            </Tab.List>
          </div>
          <Tab.Panels className="">
            <Tab.Panel key="topScorersDaily">
              <section className="flex flex-col ">
                <ol className="space-y-2">
                  {sortedTodayList.map((scorer, index) => (
                    <li
                      key={`holders-${index}`}
                      className={classNames(
                        "flex justify-between items-center rounded-lg shadow-xl px-6 py-3  transition duration-500 object-cover bg-white/80"
                      )}
                    >
                      <a
                        href={`https://t.me/${scorer.account}/`}
                        target="_blank"
                        className={classNames("text-[#747474] text-[14px]")}
                      >
                        {index + 1}. {scorer.account}
                      </a>
                      <p className="text-right text-[#747474] text-[16px]">
                        {prettyValue(scorer.score)} points
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            </Tab.Panel>
            <Tab.Panel key="topScorersWeekly">
              <section className="flex flex-col ">
                <ol className="space-y-2">
                  {sortedSundayList.map((scorer, index) => (
                    <li
                      key={`holders-${index}`}
                      className={classNames(
                        "flex justify-between items-center rounded-lg shadow-xl px-6 py-3  transition duration-500 object-cover bg-white/80"
                      )}
                    >
                      <a
                        href={`https://t.me/${scorer.account}/`}
                        target="_blank"
                        className={classNames("text-[#747474] text-[14px]")}
                      >
                        {index + 1}. {scorer.account}
                      </a>
                      <p className="text-right text-[#747474] text-[16px]">
                        {prettyValue(scorer.score)} points
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            </Tab.Panel>
            <Tab.Panel key="topScorersAlltime">
              <section className="flex flex-col ">
                <ol className="space-y-2">
                  {sortedAlltimeList.map((scorer, index) => (
                    <li
                      key={`holders-${index}`}
                      className={classNames(
                        "flex justify-between items-center rounded-lg shadow-xl px-6 py-3  transition duration-500 object-cover bg-white/80"
                      )}
                    >
                      <a
                        href={`https://t.me/${scorer.account}/`}
                        target="_blank"
                        className={classNames("text-[#747474] text-[14px]")}
                      >
                        {index + 1}. {scorer.account}
                      </a>
                      <p className="text-right text-[#747474] text-[16px]">
                        {prettyValue(scorer.score)} points
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
        <img
          src="/images/celeb.png"
          className="absolute -bottom-0 left-0 right-0 mx-auto"
        />
      </div>
    </div>
  );
}
